import { useEffect, useMemo, useState } from "react";
import type { StudyGroupDto } from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { validateGroups, type GroupDraft } from "../../lib/groups.js";
import { Alert, ErrorAlert } from "../ui/index.js";

let keySeq = 0;
const toDraft = (g: StudyGroupDto): GroupDraft => ({ key: g.id, id: g.id, code: g.code, name: g.name, minAge: g.minAge, maxAge: g.maxAge, sortOrder: g.sortOrder });

/**
 * Editor de grupos de edad. Valida en cliente (rangos enteros, sin solapamientos) y aplica los
 * cambios en orden: eliminaciones → actualizaciones → altas. El servidor vuelve a validar.
 */
export function GroupsEditor({
  studyId,
  groups,
  locked,
  demoLocked = false,
  onSaved,
}: {
  studyId: string;
  groups: StudyGroupDto[];
  locked: boolean;
  /** Modo demo: solo lectura. */
  demoLocked?: boolean;
  onSaved: () => Promise<void>;
}) {
  const [drafts, setDrafts] = useState<GroupDraft[]>(() => groups.map(toDraft));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDrafts(groups.map(toDraft)), [groups]);
  const problems = useMemo(() => validateGroups(drafts), [drafts]);

  const update = (key: string, patch: Partial<GroupDraft>) => {
    setSaved(false);
    setDrafts((d) => d.map((g) => (g.key === key ? { ...g, ...patch } : g)));
  };

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const keep = new Set(drafts.filter((d) => d.id).map((d) => d.id));
      for (const g of groups) if (!keep.has(g.id)) await api.deleteGroup(studyId, g.id);
      for (const d of drafts) {
        const body = { code: d.code, name: d.name.trim(), minAge: d.minAge, maxAge: d.maxAge, sortOrder: d.sortOrder };
        if (d.id) {
          const orig = groups.find((g) => g.id === d.id);
          if (orig && (orig.code !== body.code || orig.name !== body.name || orig.minAge !== body.minAge || orig.maxAge !== body.maxAge || orig.sortOrder !== body.sortOrder)) {
            await api.updateGroup(studyId, d.id, body);
          }
        } else await api.createGroup(studyId, body);
      }
      await onSaved();
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e));
      await onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-3" aria-labelledby="groups-title">
      <div>
        <h2 id="groups-title" className="text-lg font-semibold">
          Grupos de edad
        </h2>
        <p className="text-sm text-slate-600">
          Rangos enteros e inclusivos (años cumplidos). No pueden solaparse: una edad fuera de todos los grupos no se registra en las comparaciones.
        </p>
      </div>
      {demoLocked && <Alert tone="warning">Modo demo: los grupos de edad no se pueden cambiar.</Alert>}
      {locked && !demoLocked && (
        <Alert tone="warning">El estudio ya tiene sesiones: cambiar rangos puede afectar la interpretación. El servidor puede rechazar cambios a grupos con sesiones.</Alert>
      )}
      <fieldset disabled={demoLocked} className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-1 pr-2">Código</th>
              <th className="py-1 pr-2">Nombre</th>
              <th className="py-1 pr-2">Edad mínima</th>
              <th className="py-1 pr-2">Edad máxima</th>
              <th className="py-1 pr-2">Orden</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {drafts.map((g, i) => (
              <tr key={g.key}>
                <td className="py-1 pr-2">
                  <input aria-label={`Código del grupo ${i + 1}`} className="input font-mono" value={g.code} onChange={(e) => update(g.key, { code: e.target.value.trim() })} />
                </td>
                <td className="py-1 pr-2">
                  <input aria-label={`Nombre del grupo ${i + 1}`} className="input" value={g.name} onChange={(e) => update(g.key, { name: e.target.value })} />
                </td>
                <td className="py-1 pr-2">
                  <input aria-label={`Edad mínima del grupo ${i + 1}`} type="number" min={0} max={130} step={1} className="input w-24" value={g.minAge} onChange={(e) => update(g.key, { minAge: Number(e.target.value) })} />
                </td>
                <td className="py-1 pr-2">
                  <input aria-label={`Edad máxima del grupo ${i + 1}`} type="number" min={0} max={130} step={1} className="input w-24" value={g.maxAge} onChange={(e) => update(g.key, { maxAge: Number(e.target.value) })} />
                </td>
                <td className="py-1 pr-2">
                  <input aria-label={`Orden del grupo ${i + 1}`} type="number" min={0} step={1} className="input w-20" value={g.sortOrder} onChange={(e) => update(g.key, { sortOrder: Number(e.target.value) })} />
                </td>
                <td className="py-1">
                  <button type="button" className="btn-secondary" onClick={() => setDrafts((d) => d.filter((x) => x.key !== g.key))} aria-label={`Quitar grupo ${g.code}`}>
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>
      {problems.length > 0 && (
        <Alert tone="warning" title="Revisa los grupos">
          <ul className="list-disc pl-5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      <ErrorAlert error={error} />
      {saved && <Alert tone="success">Grupos guardados.</Alert>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={demoLocked}
          onClick={() => {
            const maxAge = Math.max(0, ...drafts.map((d) => d.maxAge));
            setDrafts((d) => [...d, { key: `new-${++keySeq}`, code: `grupo${d.length + 1}`, name: `${maxAge + 1}–${maxAge + 10} años`, minAge: maxAge + 1, maxAge: maxAge + 10, sortOrder: d.length }]);
          }}
        >
          Agregar grupo
        </button>
        <button type="button" className="btn-primary" disabled={demoLocked || problems.length > 0 || busy} onClick={save}>
          {busy ? "Guardando…" : "Guardar grupos"}
        </button>
      </div>
    </section>
  );
}
